import { inject, Injectable } from "@angular/core";
import { QueryClient } from "@tanstack/angular-query-experimental";
import { SheetsApiService } from "@dis/gapi";
import { createCollection } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";

export class WorkFlowDefinition {
  sequenceOrder: number | null;
  name: string | null;
  displayName: string | null;
  description: string | null;
  durationEstimateInDays: number | null;
  rowIndex: number | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedBy: string | null;

  constructor(data: Partial<WorkFlowDefinition> = {}) {
    this.sequenceOrder = data.sequenceOrder || null;
    this.name = data.name || null;
    this.displayName = data.displayName || null;
    this.description = data.description || null;
    this.durationEstimateInDays = data.durationEstimateInDays || null;
    this.rowIndex = data.rowIndex || null;
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
    this.updatedBy = data.updatedBy || null;
  }
}

@Injectable({ providedIn: "root" })
export class WorkflowCollectionService {
  queryClient = inject(QueryClient);

  #workflowSheetService = inject(WorkflowSheetService);

  getWorkflowCollectionByToolTypeName(toolTypeName: string) {
    return createCollection(
      queryCollectionOptions({
        queryKey: ["workflow", toolTypeName],
        queryFn: async () => await this.#workflowSheetService.getCollection(toolTypeName),
        queryClient: this.queryClient,
        getKey: (workFlow: WorkFlowDefinition) => workFlow.rowIndex!,
      })
    );
  }
}

@Injectable({ providedIn: "root" })
export class WorkflowSheetService {
  #sheetsApiService = inject(SheetsApiService);

  #spreadsheetId = "1_pCBaHtQIp-STMtWeo3f8-78pl6YdQJQc0WMCVMBGvw";
  #collectionRows = "1:1000";

  async getCollection(toolTypeName: string): Promise<WorkFlowDefinition[]> {
    const collectionRange = `${toolTypeName}!${this.#collectionRows}`;
    return await this.#sheetsApiService
      .getRangeValues(this.#spreadsheetId, collectionRange)
      .then((data) => this.#transformSheetDataToJsonArray(data) as WorkFlowDefinition[])
      .catch((error) => {
        console.error;
        return [];
      });
  }

  #transformSheetDataToJsonArray(data: any[][]): any[] {
    const headers = data.shift()!;
    return data.map((row, index) => {
      return headers.reduce(
        (accumulator, currentValue, currentIndex) => {
          accumulator[currentValue.toString().trim()] = row[currentIndex];
          return accumulator;
        },
        {
          rowIndex: index + 2, // This allows us to start at row2
        }
      );
    });
  }
}
